import React, { useEffect, useState } from 'react';
import { Paper, Box, Button, Typography } from '@mui/material';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { formatPhoneNumberToE164 } from '../../../utils/helpers';

import { StyledDivider, useBookingModalStyles } from '../styles';
import { useBookingsContext } from '../../../contexts/BookingsContext';
import HomeOwnerContact from './HomeOwnerContact';
import HomeAddress from './HomeAddress';
import { IUserInfo } from '../../../global/interfaces';

const BookingDetails = ({
  handleCloseBookingModal,
}: {
  handleCloseBookingModal: (redirect?: string | undefined) => void;
}) => {
  const styles = useBookingModalStyles();

  const {
    selectedPlan,
    setBookingModalUrl,
    editBooking,
    setEditBooking,
    userInfo,
    setUserInfo,
  } = useBookingsContext();

  const [submitted, setSubmitted] = useState(false);
  const optionalAddress = selectedPlan?.optional_address;

  const { values, handleChange, errors, handleSubmit, isValid, setFieldValue } =
    useFormik({
      initialValues: {
        firstname: userInfo?.firstname || '',
        lastname: userInfo?.lastname || '',
        email: userInfo?.email || '',

        mobile: editBooking?.mobile_number || '',
        address: editBooking?.address || '',
        city: editBooking?.city || '',
        postalCode: editBooking?.postal_code || '',
        province: editBooking?.province || '',
        country: 'Canada',
      },
      validationSchema: Yup.object({
        firstname: Yup.string().required('First Name is required'),
        lastname: Yup.string().required('Last Name is required'),
        email: Yup.string()
          .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, 'Invalid email address')
          .required('Email is required'),
        mobile: Yup.string()
          .matches(
            /^\+1\s*(?:\(?(\d{3})\)?[\s-]*)?(\d{3})[\s-]?(\d{4})$/,
            'Mobile number must start with "+1" and have 11 digits',
          )
          .required('Mobile number is required'),
        address: optionalAddress
          ? Yup.string()
          : Yup.string().required('Address is required'),
        city: optionalAddress
          ? Yup.string()
          : Yup.string().required('City is required'),
        postalCode: optionalAddress
          ? Yup.string()
          : Yup.string().required('Postal code is required').max(8),
        province: optionalAddress
          ? Yup.string()
          : Yup.string().required('Province is required'),
        country: optionalAddress
          ? Yup.string()
          : Yup.string().required('Country is required'),
      }),
      onSubmit: async () => {
        console.log('::::onSubmit:', editBooking);

        if (!editBooking) {
          alert('Unable to update booking, please try to create a new one.');
          return;
        }

        const {
          email,
          firstname,
          lastname,
          mobile,
          address,
          postalCode,
          city,
          province,
        } = values;

        setUserInfo({
          firstname,
          lastname,
          email,
        } as IUserInfo);

        editBooking.mobile_number = formatPhoneNumberToE164(mobile);
        editBooking.address = address;
        editBooking.postal_code = postalCode;
        editBooking.city = city;
        editBooking.province = province;
        editBooking.country_code = 'CA';
        setEditBooking({ ...editBooking });

        setBookingModalUrl(`booking-confirmation`);
      },
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: true,
    });

  return (
    <Box id="booking-details-container" sx={styles.container}>
      <Typography sx={{ ...styles.title, flexShrink: 0 }}>
        <Button
          sx={{
            ...styles.button,
            ...styles.close,
          }}
          onClick={() => handleCloseBookingModal()}>
          Close
        </Button>
        Enter Contact Details
      </Typography>

      <Paper elevation={0} sx={styles.paper}>
        <HomeOwnerContact
          values={values}
          errors={errors}
          setFieldValue={setFieldValue}
          submitted={submitted}
          setSubmitted={setSubmitted}
        />

        <StyledDivider sx={{ margin: '2rem 0', ml: '-1rem' }} />
        <HomeAddress
          handleChange={handleChange}
          values={values}
          errors={errors}
          optionalAddress={optionalAddress}
        />
      </Paper>

      <Box sx={styles.buttonContainer}>
        <Button
          sx={styles.button}
          onClick={() => setBookingModalUrl('booking-spot')}>
          Back
        </Button>
        <Button
          sx={{
            ...styles.button,
            ...styles.buttonPrimary,
            ...(!isValid && { opacity: '50%' }),
          }}
          onClick={() => {
            setSubmitted(true);
            handleSubmit();
          }}>
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default BookingDetails;
